// https://my.tealiumiq.com/tms?utk=028817c077d88871bdc44ece71708789a92c3a50258d712ed9
import { Injectable } from '@angular/core';
import { DomService, IAnalyticsWindow } from '@nibtravel/shared/util/dom';
import {
  keyBy,
  mapValues,
  orderBy,
  map,
  join,
  find,
  get,
  merge,
  values,
  sortBy,
} from 'lodash';
import { Store } from '@ngxs/store';
import { format } from 'date-fns';
import { AppConfigState } from '@nibtravel/shared/util/app-config';
import { IQuoteInput, QuoteState } from '@nibtravel/shared/data-access/quote';
import { PlansState } from '@nibtravel/shared/data-access/plans';
import { UnderwriterSelector } from '@nibtravel/shared/data-access/underwriter';
import { CountriesState } from '@nibtravel/shared/data-access/countries';
import {
  IBenefitSummary,
  IDestinationCountry,
  IQuotePriceSummary,
} from '@nibtravel/shared/util/models';

export enum TealiumEvent {
  EMAIL_YOUR_QUOTE = 'email_your_quote',
  INIT = 'init',
  SUBSCRIBE_MAILING_LIST = 'subscribe_to_mailing_list',
}

@Injectable({
  providedIn: 'root',
})
export class TealiumService {
  _tealiumScriptAddress: string;
  _window: IAnalyticsWindow;
  constructor(private _domService: DomService, private _store: Store) {
    this._window = this._domService.getTeamliumAnalyticsWindow();
    this._tealiumScriptAddress = this._store.selectSnapshot(
      AppConfigState.getTealiumScriptAddress
    );
  }

  init(): void {
    this.loadTealiumScript();
  }
  private loadTealiumScript(): void {
    this._domService.setTealiumUtagDataLayer({});

    const tealiumScript = this._window.document.createElement('script');
    tealiumScript.src = this._tealiumScriptAddress;
    tealiumScript.type = 'text/java' + 'script';
    tealiumScript.async = true;
    const script = this._window.document.getElementsByTagName('script')[0];
    // @ts-ignore
    script.parentNode.insertBefore(tealiumScript, script);
    this._window.utag_cfg_ovrd = this._window.utag_cfg_ovrd || {};
    this._window.utag_cfg_ovrd.noview = true;
  }

  trackView(variables: any) {
    // @ts-ignore
    this._window.utag.view(variables);
  }

  trackEvent(type: TealiumEvent) {
    const data = this.constructData(type);
    // @ts-ignore
    this._window.utag.link(data);
  }

  private constructData(type: TealiumEvent) {
    let utag_data = {};
    switch (type) {
      case TealiumEvent.EMAIL_YOUR_QUOTE:
        utag_data = this.getEmailQuoteData();
        return { ...utag_data, tealium_event: TealiumEvent.EMAIL_YOUR_QUOTE };

      case TealiumEvent.SUBSCRIBE_MAILING_LIST:
        utag_data = this.getSubscribeToMailingList();
        return {
          ...utag_data,
          tealium_event: TealiumEvent.SUBSCRIBE_MAILING_LIST,
        };
      default:
        utag_data = {};
        return utag_data;
    }
  }

  private getEmailQuoteData() {
    const quote = this._store.selectSnapshot(QuoteState.quote);
    const currentRoute = get(quote, 'currentRoute', '/');
    const underwritersMap = this._store.selectSnapshot(
      UnderwriterSelector.underwritersMap
    );
    const contractsMap = this._store.selectSnapshot(
      UnderwriterSelector.contractsMap
    );
    let quotePrices = this._store.selectSnapshot(QuoteState.quotePrices);
    const quoteId = get(quote, 'id');
    const input = get(quote, 'input');
    const homeCountryCode = get(input, 'homeAddress.countryCode');
    const plans = this._store.selectSnapshot(PlansState.getState);
    const selectedPlanId = get(quote, 'input.planId') as number;

    // when not in quote/plans page, the plan is selected
    if (currentRoute !== '/quote/plans') {
      quotePrices = quotePrices.filter((p) => p.planId === selectedPlanId);
    }

    const availablePlanId = get(
      quote,
      'input.planId',
      quotePrices[0].planId
    ) as string;

    const planSummariesByPlanIdKeyObj = get(plans, 'planSummaries');
    const targetPlanSummary = get(planSummariesByPlanIdKeyObj, availablePlanId);
    const insuranceContractId = get(targetPlanSummary, 'insuranceContractId');
    const languageCode = get(
      contractsMap,
      `[${insuranceContractId}].languageCode`
    );
    const pricesByPlanIdKeyObj = keyBy(get(quote, 'prices'), 'planId');
    const mergedPlanData = mapValues(
      planSummariesByPlanIdKeyObj,
      (value, key) => {
        return merge({}, value, pricesByPlanIdKeyObj[key]);
      }
    );

    const planSpecificData = this.getPlanSpecificData(
      quotePrices,
      mergedPlanData,
      quoteId as string,
      currentRoute
    );
    // @ts-ignore
    const underwriterObject = find(underwritersMap, (underwriter) => {
      return find(underwriter.insuranceContracts, { insuranceContractId });
    });

    const quoteCreateDate = format(Date.now(), 'yyyy/MM/dd, HH:mm');
    const departureDate = format(
      new Date(get(input, 'departureDate', '')),
      'yyyy/MM/dd'
    );
    const returnDate = format(
      new Date(get(input, 'coverEndDate', '')),
      'yyyy/MM/dd'
    );
    const dataOnQuotePage = this.getDataOnQuotePage(quotePrices);
    const data = {
      brand_name: 'Travel Insurance Direct',
      customer_email: get(input, 'emailAddressForQuote', ''), // Required
      customer_first_name: '', // Suggested
      customer_last_name: '', // Suggested
      has_emailed_quote: 'true', // Required what is the value
      quote_created_date: quoteCreateDate, // Required
      quote_culture_code: languageCode, // Suggested
      quote_departure_date: departureDate, // Required
      quote_donation_amount: get(input, 'footprintsDonation.donationAmount', 0), // Suggested
      quote_donation_currency:
        mergedPlanData[quotePrices[0].planId]?.currencyCode, // Suggested
      quote_donation_enabled: 'true', // Suggested must keep it or set to true/false
      quote_id: quoteId, // Required
      quote_origin_country_code: homeCountryCode, // Required
      quote_partner_code: '', // Suggested
      ...planSpecificData,
      quote_price_count:
        currentRoute === '/quote/plans' ? quotePrices.length : '1', // Required
      quote_return_date: returnDate, // Required
      quote_travellers_age: map(
        get(input, 'policyHolders'),
        (holders) => holders.age
      ).join(','), // Required
      quote_trip_destination: join(get(input, 'destinationCountryCodes'), ','), // Required
      quote_type: get(input, 'quoteType'), // Required
      quote_underwriter_code: get(underwriterObject, 'code', ''), // Required
      quote_underwriter_name: get(underwriterObject, 'name', ''), // Required
      ...dataOnQuotePage,
    };
    return data;
  }

  private getPlanSpecificData(
    quotePrices: IQuotePriceSummary[],
    mergedPlanData: Record<string, any>,
    quoteId: string,
    currentRoute: string
  ) {
    const planSpecificData: Record<string, string> = {};
    const appUrl = this._store.selectSnapshot(AppConfigState.getAppUrl);
    const displayPrice =
      currentRoute === '/quote/plans' ? 'basePrice.price' : 'totalPrice';
    quotePrices.forEach((plan, index: number) => {
      const planData = mergedPlanData[plan.planId];
      const domain = new URL(appUrl).origin;
      const quotePriceUrl = `${domain}/quote/${quoteId}/${plan.planId}/options`;
      planSpecificData[`quote_price${index + 1}_amount`] = get(
        planData,
        displayPrice
      );
      planSpecificData[`quote_price${index + 1}_currency`] =
        planData.currencyCode;
      planSpecificData[`quote_price${index + 1}_plan_id`] = planData.planId;
      planSpecificData[`quote_price${index + 1}_plan_name`] = planData.name;
      planSpecificData[`quote_price${index + 1}_url`] = quotePriceUrl;
    });
    return planSpecificData;
  }
  private getDataOnQuotePage(quotePrices: IQuotePriceSummary[]) {
    const benefitsSummary = this._store.selectSnapshot(
      PlansState.getPlanBenefitsSummary
    );

    const benefitCodeToPropertyName: Record<string, string> = {
      OVERSEASMEDICALINCLCOVID: 'overseas_medical_including_covid',
      RENTALVEHICLEINSURANCEEXCESS: 'rentalvechicleinsuranceexcess',
      CANCELLATIONDEFERMENT: 'cancellationdeferment',
      LUGGAGEPERSONALEFFECTS: 'luggagepersonaleffects',
    };

    const benefitCodesForEmailYourQuote = Object.keys(
      benefitCodeToPropertyName
    );

    const filteredArray = benefitsSummary?.filter((item) =>
      benefitCodesForEmailYourQuote.includes(item?.code)
    );

    const keyByBenefitCode: Record<string, IBenefitSummary> = keyBy(
      filteredArray,
      'code'
    );

    const data: Record<string, string> = {};

    for (const benefitCode in benefitCodeToPropertyName) {
      const propertyName = benefitCodeToPropertyName[benefitCode];
      const benefitSummary = keyByBenefitCode[benefitCode];
      quotePrices.forEach((quotePriceSummary, index) => {
        // @ts-ignore
        data[`quote${index + 1}_${propertyName}`] =
          benefitSummary?.limitComparison?.[quotePriceSummary.planId];
      });
    }
    return data;
  }
  private getSubscribeToMailingList() {
    const quote = this._store.selectSnapshot(QuoteState.quote);
    const plans = this._store.selectSnapshot(PlansState.getState);
    const planSummariesByPlanIdKeyObj = get(plans, 'planSummaries');
    const firstPlanId = get(quote, 'prices[0].planId');
    const availablePlanId = get(quote, 'input.planId', firstPlanId) as string;
    const targetPlanSummary = get(planSummariesByPlanIdKeyObj, availablePlanId);
    const insuranceContractId = get(targetPlanSummary, 'insuranceContractId');
    const contractsMap = this._store.selectSnapshot(
      UnderwriterSelector.contractsMap
    );
    const languageCode = get(
      contractsMap,
      `[${insuranceContractId}].languageCode`
    );
    const input = get(quote, 'input');
    const homeCountryCode = get(input, 'homeAddress.countryCode');
    const data = {
      brand_name: 'Travel Insurance Direct',
      customer_country_of_residence: homeCountryCode,
      consent_timestamp: new Date().toISOString(), // Required
      customer_culture_code: languageCode, // Suggested
      customer_email: get(input, 'emailAddressForQuote', ''), // Required
      customer_first_name: '', // Suggested if available
      customer_last_name: '', // Suggested if available
      form_source: 'EmailQuote', // Suggested eg: Scholarships or the ID or the id of the form  (site section)
      mailing_list_frequency: '',
      mailing_list_source: 'newsletter, offers, feedback', // the mailing list it goes into  newsletter feedback scholarships offers
      has_consent_policy: 'true', // Required
      has_consent_promo: 'true', // Suggested
      consent_promo_date: new Date().toISOString(),
    };
    return data;
  }
}
